import { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import {
	Box,
	Button,
	CircularProgress,
	Typography,
	Stack,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import useSalesforceReportAnalysis from 'hooks/useSalesforceReportAnalysis';
import SuggestedRulesList from '../components/AlertCenter/SuggestedRulesList';
import CustomizeReportAlertForm from '../components/AlertCenter/CustomizeReportAlertForm';
import inputSignalRulesService from 'services/inputSignalRulesService';
import { useNotificationContext } from 'contexts/NotificationContext';
import PrimaryButton from '../components/Common/Buttons/PrimaryButton';
import SecondaryButton from '../components/Common/Buttons/SecondaryButton';
import { useIRWorkstation } from 'features/IRWorkstation/contexts/IRWorkstationContext';

// Track Events
import { useTrackEvent } from 'hooks/useTrackEvent';
import { ButtonClickTargets } from 'constants/buttonClickTargets';
import { PageLoadsTargets } from 'constants/pageLoadTargets';
import { validateReportAlert } from '../utils/reportAlertValidation';

const CenteredContent = ({ children }) => {
	return (
		<Box
			sx={{
				flex: 1,
				minHeight: 400,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				textAlign: 'center',
				px: 3,
				pb: 32, // slightly offsets the content above true center
			}}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					maxWidth: 460,
				}}
			>
				{children}
			</Box>
		</Box>
	);
};

const LoadingState = ({ reportName }) => {
	return (
		<CenteredContent>
			<CircularProgress size={44} thickness={4} sx={{ mb: 3 }} />

			<Typography
				variant='h1'
				sx={{
					fontWeight: 700,
					mb: 2,
				}}
			>
				Analyzing report...
			</Typography>

			<Typography
				variant='body2'
				color='text.secondary'
				sx={{
					lineHeight: 1.6,
				}}
			>
				Reading fields, groupings, and thresholds in{' '}
				<Typography
					component='span'
					variant='inherit'
					sx={{
						fontWeight: 700,
						color: 'text.primary',
					}}
				>
					{reportName}
				</Typography>{' '}
				to generate suggested alerts.
			</Typography>

			<Typography
				variant='body2'
				color='text.secondary'
				sx={{
					lineHeight: 1.6,
					mt: 2,
				}}
			>
				This may take a few minutes.
			</Typography>
		</CenteredContent>
	);
};

const ErrorState = ({ reportName }) => {
	return (
		<CenteredContent>
			<ErrorOutlineIcon fontSize='large' color='error' />

			<Typography
				variant='h1'
				sx={{
					fontWeight: 700,
					mb: 2,
				}}
			>
				Unable to generate suggested alerts
			</Typography>

			<Typography
				color='text.secondary'
				sx={{
					lineHeight: 1.6,
				}}
			>
				We were unable to generate suggested alerts for{' '}
				<Typography
					component='span'
					variant='inherit'
					fontWeight={700}
					color='text.primary'
				>
					{reportName}.
				</Typography>{' '}
				Please try again later.
			</Typography>
		</CenteredContent>
	);
};

const GenerateAlertFromReport = () => {
	const { tenant } = useIRWorkstation();
	const { showNotification } = useNotificationContext();
	const navigate = useNavigate();
	const { trackButtonClick, trackPageLoad } = useTrackEvent({
		tenant,
		product: 'IEM',
	});

	const { reportId } = useParams();
	const { state } = useLocation();

	// Tracks which suggestions are selected
	const [selectedRules, setSelectedRules] = useState([]);
	// Stores user edits without modifying the original suggestions
	const [customizedRules, setCustomizedRules] = useState(new Map());
	// Stores validation errors for each selected alert
	const [validationErrors, setValidationErrors] = useState(new Map());
	// Tracks which alert is currently being tested
	const [testingRules, setTestingRules] = useState(new Map());
	// Tracks the bulk alert creation request
	const [isSubmitting, setIsSubmitting] = useState(false);
	const reportName = state?.reportName || 'this report';

	const { suggestedRules, loading, error } =
		useSalesforceReportAnalysis(reportId);

	const handleButtonClickTrackEvent = (targetKey) => {
		const targetMap = {
			selectRule: ButtonClickTargets.AlertCenterSuggestedSelect,
			deselectRule: ButtonClickTargets.AlertCenterSuggestedDeselect,
			backToReports: ButtonClickTargets.AlertCenterSuggestedBackToReports,
			ruleName: ButtonClickTargets.AlertCenterSuggestedRuleName,
			ruleSeverity: ButtonClickTargets.AlertCenterSuggestedRuleSeverity,
			ruleVisibility: ButtonClickTargets.AlertCenterSuggestedRuleVisibility,
			cancel: ButtonClickTargets.AlertCenterSuggestedCancel,
			createRule: ButtonClickTargets.AlertCenterSuggestedCreateRule,
		};

		const target = targetMap[targetKey];
		if (target) {
			trackButtonClick(target);
		}
	};

	//  Default the first suggestion to selected after suggestions
	// are initially generated
	useEffect(() => {
		// Clear validation and testing state when a new set of suggestions is generated
		setValidationErrors(new Map());
		setTestingRules(new Map());

		if (suggestedRules?.length > 0) {
			const firstRule = suggestedRules[0];

			setSelectedRules([firstRule]);

			setCustomizedRules(
				new Map([
					[
						firstRule.suggestion_id,
						{
							...firstRule,
							title: firstRule.title || '',
							severity: firstRule.severity || '',
							visibility: firstRule.visibility || 'local',
						},
					],
				])
			);
		} else {
			setSelectedRules([]);
			setCustomizedRules(new Map());
		}
	}, [suggestedRules]);

	useEffect(() => {
		trackPageLoad(PageLoadsTargets.NewAlertAnalysis);
	}, []);

	const handleSuggestionSelectionChange = (rule) => {
		const ruleId = rule.suggestion_id;

		const isSelected = selectedRules.some(
			(selectedRule) => selectedRule.suggestion_id === ruleId
		);

		setSelectedRules((currentRules) => {
			if (isSelected) {
				handleButtonClickTrackEvent('deselectRule');
				return currentRules.filter(
					(selectedRule) => selectedRule.suggestion_id !== ruleId
				);
			}

			handleButtonClickTrackEvent('selectRule');

			return [...currentRules, rule];
		});

		if (isSelected) {
			setValidationErrors((currentErrors) => {
				const nextErrors = new Map(currentErrors);
				nextErrors.delete(ruleId);
				return nextErrors;
			});
		}

		setCustomizedRules((currentRules) => {
			const nextRules = new Map(currentRules);

			if (!nextRules.has(ruleId)) {
				nextRules.set(ruleId, {
					...rule,
					title: rule.title || '',
					severity: rule.severity || '',
					visibility: rule.visibility || 'local',
				});
			}

			return nextRules;
		});
	};

	const handleSelectAll = () => {
		setSelectedRules(suggestedRules);

		setCustomizedRules((currentRules) => {
			const nextRules = new Map(currentRules);

			suggestedRules.forEach((rule) => {
				const ruleId = rule.suggestion_id;

				if (!nextRules.has(ruleId)) {
					nextRules.set(ruleId, {
						...rule,
						title: rule.title || '',
						severity: rule.severity || '',
						visibility: rule.visibility || 'local',
					});
				}
			});

			return nextRules;
		});
	};

	const handleDeselectAll = () => {
		setSelectedRules([]);
		setValidationErrors(new Map());
	};

	const handleAlertChange = (rule, updatedAlert) => {
		const ruleId = rule.suggestion_id;

		setCustomizedRules((currentRules) => {
			const nextRules = new Map(currentRules);
			nextRules.set(ruleId, updatedAlert);

			return nextRules;
		});

		const ruleErrors = validateReportAlert(updatedAlert);
		const hasErrors = Object.keys(ruleErrors).length > 0;

		setValidationErrors((currentErrors) => {
			const nextErrors = new Map(currentErrors);

			if (hasErrors) {
				nextErrors.set(ruleId, ruleErrors);
			} else {
				nextErrors.delete(ruleId);
			}

			return nextErrors;
		});
	};

	const buildAlertPayload = (alert) => ({
		report_id: reportId,
		title: alert.title?.trim() || '',
		severity: alert.severity?.toLowerCase() || '',
		visibility: alert.visibility || 'local',
		message: alert.message?.trim() || '',
		category: alert.category || '',
		signal_type: alert.signal_type || '',
		metric: alert.metric,
		select: alert.select,
		target_kind: alert.target_kind || '',
		scope: alert.scope || 'per_entity',
	});

	const handleTestAlert = async (rule) => {
		const ruleId = rule.suggestion_id;

		const alert = customizedRules.get(ruleId) ?? {
			...rule,
			title: rule.title || '',
			severity: rule.severity || '',
			visibility: rule.visibility || 'local',
		};

		setTestingRules((current) => new Map(current).set(ruleId, true));

		try {
			const { record_count, alerts_count } =
				await inputSignalRulesService.testInputSignalRule(
					buildAlertPayload(alert)
				);

			const recordCountMessage =
				record_count == null
					? 'record count unavailable'
					: `${record_count} records returned`;

			showNotification(
				`Successful Alert Test. The underlying report ran successfully - ${recordCountMessage}. Based on the current data, this alert would create ${alerts_count} on the next scheduled run.`,
				'success',
				null // Keep notification open until manually dismissed
			);
		} catch (err) {
			console.error('Failed to test alert:', err);
			showNotification(
				`Unsuccessful Alert Test. ${err.message}`,
				'error',
				null // Keep notification open until manually dismissed
			);
		} finally {
			setTestingRules((current) => {
				const next = new Map(current);
				// removes that alert from the set of alerts currently being tested
				next.delete(ruleId);
				return next;
			});
		}
	};

	const handleAlertCreation = async () => {
		handleButtonClickTrackEvent('createRule');

		if (selectedRules.length === 0 || isSubmitting) {
			return;
		}

		const errors = new Map();

		selectedRules.forEach((rule) => {
			const ruleId = rule.suggestion_id;
			const alert = customizedRules.get(ruleId) ?? rule;
			const ruleErrors = validateReportAlert(alert);

			if (Object.keys(ruleErrors).length > 0) {
				errors.set(ruleId, ruleErrors);
			}
		});

		setValidationErrors(errors);

		if (errors.size > 0) {
			return;
		}

		const alertsToCreate = selectedRules.map((rule) => {
			const alert = customizedRules.get(rule.suggestion_id) ?? rule;

			return buildAlertPayload(alert);
		});

		setIsSubmitting(true);

		try {
			await inputSignalRulesService.createInputSignalRulesBulk(alertsToCreate);

			showNotification(
				alertsToCreate.length === 1
					? 'Alert created successfully'
					: `${alertsToCreate.length} alerts created successfully`,
				'success'
			);

			navigate('..');
		} catch (err) {
			console.error('Failed to create alerts:', err);
			showNotification(getCreateAlertsErrorMessage(err, alertsToCreate.length), 'error');
		} finally {
			setIsSubmitting(false);
		}
	};

	const getCreateAlertsErrorMessage = (err, count) => {
		if (err.statusCode === 400 || err.statusCode === 422) {
			return err.message;
		}
		if (err.statusCode === 403) {
			return 'You do not have the correct permissions for the report related to this alert and cannot subscribe to it';
		}
		return count === 1 ? 'Failed to create alert' : 'Failed to create alerts';
	};

	return (
		<Box
			sx={{
				flex: 1,
				minWidth: 0,
				overflowY: 'auto',
				backgroundColor: 'background.default',
				padding: '12px 20px',
				display: 'flex',
				flexDirection: 'column',
				gap: '14px',
			}}
		>
			<Button
				disabled={loading} // disabled during analysis until we know how we want to handle 'canceling' request
				startIcon={<ArrowBackIcon />}
				onClick={() => {
					handleButtonClickTrackEvent('backToReports');
					navigate('../new');
				}}
				sx={{ alignSelf: 'flex-start' }}
			>
				Reports
			</Button>

			{/* Loading state */}
			{loading && <LoadingState reportName={reportName} />}

			{/* Error state */}
			{!loading && error && <ErrorState reportName={reportName} />}

			{/* Main content displayed when not loading and no error */}
			{!loading && !error && (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 2,
					}}
				>
					<Typography variant='h1'>New alert - {reportName}</Typography>

					{suggestedRules?.length === 0 ? (
						<Typography color='text.secondary'>
							No suggested alerts were generated.
						</Typography>
					) : (
						<Stack spacing={2}>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									gap: 2,
								}}
							>
								<Typography color='text.secondary' variant='overline'>
									Suggested - select one or more, then adjust
								</Typography>

								<Box sx={{ display: 'flex', gap: 1 }}>
									<Button
										size='small'
										variant='text'
										onClick={handleSelectAll}
										disabled={
											!suggestedRules?.length ||
											selectedRules.length === suggestedRules.length
										}
									>
										Select all
									</Button>

									<Button
										size='small'
										variant='text'
										onClick={handleDeselectAll}
										disabled={selectedRules.length === 0}
									>
										Deselect all
									</Button>
								</Box>
							</Box>

							{/* List of suggested rules */}
							<SuggestedRulesList
								suggestedRules={suggestedRules}
								selectedRules={selectedRules}
								onSelectionChange={handleSuggestionSelectionChange}
							/>

							<Typography color='text.secondary' variant='overline'>
								Customize
							</Typography>

							{/* Form to customize and create alert */}
							{selectedRules.length > 0 ? (
								<Stack spacing={1}>
									{selectedRules.map((rule) => {
										const ruleId = rule.suggestion_id;

										const customizedAlert = customizedRules.get(ruleId) ?? {
											...rule,
											title: rule.title || '',
											severity: rule.severity || '',
											visibility: rule.visibility || 'local',
										};

										return (
											<CustomizeReportAlertForm
												key={ruleId}
												alert={customizedAlert}
												titleError={validationErrors.get(ruleId)?.title}
												descriptionError={validationErrors.get(ruleId)?.message}
												metricThresholdError={
													validationErrors.get(ruleId)?.metricThreshold
												}
												onChange={(updatedAlert) =>
													handleAlertChange(rule, updatedAlert)
												}
												onTest={() => handleTestAlert(rule)}
												isTesting={testingRules.get(ruleId) || false}
												onNameBlur={() =>
													handleButtonClickTrackEvent('ruleName')
												}
												onSeverityChange={() =>
													handleButtonClickTrackEvent('ruleSeverity')
												}
												onVisibilityChange={() =>
													handleButtonClickTrackEvent('ruleVisibility')
												}
											/>
										);
									})}
								</Stack>
							) : (
								<Box
									sx={{
										backgroundColor: 'background.paper',
										border: '0.5px dashed',
										borderColor: 'border.light',
										borderRadius: '8px',
										p: 3,
										textAlign: 'center',
									}}
								>
									<Typography variant='body2' color='text.secondary'>
										Select one or more suggested alerts to customize
									</Typography>
								</Box>
							)}
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'flex-end',
									gap: 1,
									mt: 1,
								}}
							>
								<SecondaryButton
									label='Cancel'
									disabled={isSubmitting}
									onClick={() => {
										handleButtonClickTrackEvent('cancel');
										navigate('..');
									}}
								/>

								<PrimaryButton
									label={
										selectedRules.length <= 1
											? 'Create alert'
											: `Create ${selectedRules.length} alerts`
									}
									loadingPosition='start'
									loading={isSubmitting}
									disabled={selectedRules.length === 0}
									onClick={handleAlertCreation}
								/>
							</Box>
						</Stack>
					)}
				</Box>
			)}
		</Box>
	);
};

export default GenerateAlertFromReport;
